export type TenantRuntimeConfigFormState = {
  tenantId: string;
  bookingSummaryCronEnabled: boolean;
  contributionReminderCronEnabled: boolean;
  niyaazReminderCronEnabled: boolean;
  sabilLedgerCronEnabled: boolean;
  sadaratAllocationReminderCronEnabled: boolean;
  sadaratAllocationReminderDaysBefore: number;
  razaEventDayOfReminderCronEnabled: boolean;
  timezone: string;
  whatsappEnabled: boolean;
  whatsappSenderKey: string;
  whatsappMessageLogRetentionDays: number;
  whatsappBotPhone: string;
  staffLeaveApplyFlowId: string;
  fmbDeliveryStopFlowId: string;
  miqaatSelfCheckinFlowId: string;
  miqaatFamilyCheckinFlowId: string;
};

export const DEFAULT_SADARAT_DAYS_BEFORE = 7;
export const DEFAULT_WHATSAPP_MESSAGE_LOG_RETENTION_DAYS = 90;
export const DEFAULT_TENANT_TIMEZONE = "Asia/Kolkata";

/** Curated fallback when Intl.supportedValuesOf('timeZone') is unavailable. */
export const FALLBACK_IANA_TIMEZONES = [
  "Asia/Kolkata",
  "Asia/Dubai",
  "Asia/Karachi",
  "Asia/Dhaka",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Europe/London",
  "Europe/Paris",
  "America/New_York",
  "America/Chicago",
  "America/Los_Angeles",
  "UTC",
] as const;

export function listIanaTimezones(): string[] {
  const supported = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] })
    .supportedValuesOf;
  if (typeof supported === "function") {
    try {
      const values = supported.call(Intl, "timeZone");
      if (Array.isArray(values) && values.length > 0) {
        // Some runtimes still list Asia/Calcutta; always keep our preferred default.
        const zones = new Set(values);
        zones.add(DEFAULT_TENANT_TIMEZONE);
        return [...zones].sort((a, b) => a.localeCompare(b));
      }
    } catch {
      // fall through
    }
  }
  return [...FALLBACK_IANA_TIMEZONES];
}

/** Ensure the current saved timezone appears in the select options. */
export function timezoneSelectOptions(current?: string | null): string[] {
  const options = listIanaTimezones();
  const resolved = resolveTimezone(current);
  if (!options.includes(resolved)) {
    return [resolved, ...options].sort((a, b) => a.localeCompare(b));
  }
  return options;
}

export function clampSadaratDaysBefore(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_SADARAT_DAYS_BEFORE;
  return Math.min(90, Math.max(1, Math.trunc(value)));
}

export function clampWhatsappMessageLogRetentionDays(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_WHATSAPP_MESSAGE_LOG_RETENTION_DAYS;
  return Math.min(365, Math.max(7, Math.trunc(value)));
}

export function resolveTimezone(value?: string | null): string {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : DEFAULT_TENANT_TIMEZONE;
}

export function toTenantRuntimeConfigFormState(
  data: Partial<
    Omit<
      TenantRuntimeConfigFormState,
      | "whatsappSenderKey"
      | "whatsappBotPhone"
      | "staffLeaveApplyFlowId"
      | "fmbDeliveryStopFlowId"
      | "miqaatSelfCheckinFlowId"
      | "miqaatFamilyCheckinFlowId"
      | "sadaratAllocationReminderDaysBefore"
      | "whatsappMessageLogRetentionDays"
      | "timezone"
    >
  > & {
    whatsappSenderKey?: string | null;
    whatsappBotPhone?: string | null;
    staffLeaveApplyFlowId?: string | null;
    fmbDeliveryStopFlowId?: string | null;
    miqaatSelfCheckinFlowId?: string | null;
    miqaatFamilyCheckinFlowId?: string | null;
    sadaratAllocationReminderDaysBefore?: number | null;
    whatsappMessageLogRetentionDays?: number | null;
    timezone?: string | null;
  }
): TenantRuntimeConfigFormState {
  const daysRaw =
    data.sadaratAllocationReminderDaysBefore === undefined ||
    data.sadaratAllocationReminderDaysBefore === null
      ? DEFAULT_SADARAT_DAYS_BEFORE
      : Number(data.sadaratAllocationReminderDaysBefore);
  const retentionRaw =
    data.whatsappMessageLogRetentionDays === undefined ||
    data.whatsappMessageLogRetentionDays === null
      ? DEFAULT_WHATSAPP_MESSAGE_LOG_RETENTION_DAYS
      : Number(data.whatsappMessageLogRetentionDays);

  return {
    tenantId: data.tenantId ?? "",
    bookingSummaryCronEnabled: Boolean(data.bookingSummaryCronEnabled),
    contributionReminderCronEnabled: Boolean(data.contributionReminderCronEnabled),
    niyaazReminderCronEnabled: Boolean(data.niyaazReminderCronEnabled),
    sabilLedgerCronEnabled: Boolean(data.sabilLedgerCronEnabled),
    sadaratAllocationReminderCronEnabled: Boolean(data.sadaratAllocationReminderCronEnabled),
    sadaratAllocationReminderDaysBefore: clampSadaratDaysBefore(daysRaw),
    razaEventDayOfReminderCronEnabled: Boolean(data.razaEventDayOfReminderCronEnabled),
    timezone: resolveTimezone(data.timezone),
    whatsappEnabled: Boolean(data.whatsappEnabled),
    whatsappSenderKey: data.whatsappSenderKey ?? "",
    whatsappMessageLogRetentionDays: clampWhatsappMessageLogRetentionDays(retentionRaw),
    whatsappBotPhone: data.whatsappBotPhone ?? "",
    staffLeaveApplyFlowId: data.staffLeaveApplyFlowId ?? "",
    fmbDeliveryStopFlowId: data.fmbDeliveryStopFlowId ?? "",
    miqaatSelfCheckinFlowId: data.miqaatSelfCheckinFlowId ?? "",
    miqaatFamilyCheckinFlowId: data.miqaatFamilyCheckinFlowId ?? "",
  };
}

export function toTenantRuntimeConfigPatch(state: TenantRuntimeConfigFormState) {
  return {
    bookingSummaryCronEnabled: state.bookingSummaryCronEnabled,
    contributionReminderCronEnabled: state.contributionReminderCronEnabled,
    niyaazReminderCronEnabled: state.niyaazReminderCronEnabled,
    sabilLedgerCronEnabled: state.sabilLedgerCronEnabled,
    sadaratAllocationReminderCronEnabled: state.sadaratAllocationReminderCronEnabled,
    sadaratAllocationReminderDaysBefore: clampSadaratDaysBefore(
      state.sadaratAllocationReminderDaysBefore
    ),
    razaEventDayOfReminderCronEnabled: state.razaEventDayOfReminderCronEnabled,
    timezone: resolveTimezone(state.timezone),
    whatsappEnabled: state.whatsappEnabled,
    whatsappSenderKey:
      state.whatsappSenderKey.trim() === "" ? null : state.whatsappSenderKey.trim(),
    whatsappMessageLogRetentionDays: clampWhatsappMessageLogRetentionDays(
      state.whatsappMessageLogRetentionDays
    ),
    whatsappBotPhone: state.whatsappBotPhone.trim() === "" ? null : state.whatsappBotPhone.trim(),
    staffLeaveApplyFlowId:
      state.staffLeaveApplyFlowId.trim() === "" ? null : state.staffLeaveApplyFlowId.trim(),
    fmbDeliveryStopFlowId:
      state.fmbDeliveryStopFlowId.trim() === "" ? null : state.fmbDeliveryStopFlowId.trim(),
    miqaatSelfCheckinFlowId:
      state.miqaatSelfCheckinFlowId.trim() === "" ? null : state.miqaatSelfCheckinFlowId.trim(),
    miqaatFamilyCheckinFlowId:
      state.miqaatFamilyCheckinFlowId.trim() === "" ? null : state.miqaatFamilyCheckinFlowId.trim(),
  };
}
